import { useEffect, useMemo, useState } from 'react'
import { Abrufausfall, useAbruf } from './Abruf'
import { api } from './api'
import { fachlicherSatz } from './Fehlersatz'
import { Dialogfenster } from './Dialogfenster'
import { beschriftung, t } from './sprache'
import { Bisvermerk } from './Bisvermerk'
import { Baumzeile, berichtsstand } from './Gliederungsbaum'
import { zielzeilen } from './Zuordnungsfenster'
import type { Bericht } from './Zuordnungsfenster'
import type { Gliederungsbild } from './typen'

/**
 * Positionen **aus Bilanz und GuV** wählen (R826, 2.13 Abschnitt 10.8).
 *
 * **Das Konto wird seit dem 2026-10-04 nicht mehr hier gewählt**, sondern im
 * Kontofeld selbst, das denselben Baum aufklappt (7.2 Abschnitt 33, R1043).
 *
 * **Der Befund des Auftraggebers vom 2026-09-27:** *„Beim Versuch ein
 * abweichendes Differenzenkonto auszuwählen, kommt nur eine flache Liste. Auch
 * hier wäre das Ziel aus der Bilanz und GuV Struktur das Zielkonto
 * auszuwählen."* Dasselbe galt für die Positionen der Abgrenzung:
 * neunundneunzig Einträge, alphabetisch, ohne die Ordnung, in der ein
 * Konzernbuchhalter arbeitet.
 *
 * **Der Baum ist nicht neu gebaut.** Das Zuordnungsfenster zeigt seit dem
 * 2026-09-18 denselben Baum mit denselben Fragen — welche Position hängt unter
 * welcher, welches Konto unter welcher Position. Seine Berechnung steht
 * deshalb dort und wird hier geliehen; ein zweiter Baum wäre eine zweite
 * Wahrheit über dieselbe Gliederung.
 *
 * **Das Fenster nennt in seinem Kopf, woran es hängt** — den Prozeß und das
 * Feld, das es füllt. Das ist die Auflage zum Preis der Entscheidung vom
 * 2026-09-27: Ein Fenster über einem Fenster verdeckt das untere, und am
 * 2026-09-26 stand die Abgrenzung schon einmal unter dem falschen Schritt.
 */
export function Gliederungswahl({
  herkunft,
  ledger,
  periode,
  gewaehlt,
  aufUebernehmen,
  aufSchliessen,
}: {
  /** Der Prozeß, zu dem dieses Fenster gehört — er steht im Kopf. */
  herkunft: string
  ledger: string
  periode: number
  /** Was schon gewählt ist — die Pfade dorthin stehen offen. */
  gewaehlt: readonly string[]
  aufUebernehmen: (schluessel: string[]) => void
  aufSchliessen: () => void
}) {
  /** Nur ein fachlicher Satz; eine Störung meldet das Störungsfenster (7.2 Abschnitt 30, R1003/R1004). */
  const [fehler, setFehler] = useState<string | null>(null)
  const [wahl, setWahl] = useState<string[]>([...gewaehlt])
  const [aufgeklappt, setAufgeklappt] = useState<ReadonlySet<string>>(new Set())

  /*
    **Der Baum ist ein Hauptabruf** (7.2 Abschnitt 39, R1211): Kam er nicht,
    steht am Ort „Ließ sich nicht laden" — oder der fachliche Satz, wenn es
    einen gibt — mit dem Knopf *Erneut versuchen* (R1210).
  */
  const bildabruf = useAbruf<Gliederungsbild | null>(
    () =>
      api.gliederung(ledger, periode).catch((f) => {
        setFehler(fachlicherSatz(f))
        throw f
      }),
    [ledger, periode],
  )
  const bild = bildabruf.wert

  /*
    **Offen wie im Bericht, und offen, was gewählt ist** (7.2 Abschnitt 55,
    R1857). Bis zum 2026-10-16 stand nur der Weg zum Gewählten offen (R826);
    jetzt steht jede Position mit Unterpositionen da, wie in der Bilanz.
  */
  useEffect(() => {
    if (bild === null) {
      return
    }

    const offen = berichtsstand(bild.knoten)
    const pfad: { schluessel: string; ebene: number }[] = []

    for (const k of bild.knoten) {
      while (pfad.length > 0 && (pfad[pfad.length - 1]?.ebene ?? 0) >= k.ebene) {
        pfad.pop()
      }

      const trifft =
        gewaehlt.includes(k.schluessel) || k.konten.some((x) => gewaehlt.includes(x.schluessel))

      if (trifft) {
        for (const a of pfad) {
          offen.add(a.schluessel)
        }
        offen.add(k.schluessel)
      }

      pfad.push({ schluessel: k.schluessel, ebene: k.ebene })
    }

    setAufgeklappt(offen)
  }, [bild, gewaehlt])

  function klappe(schluessel: string) {
    setAufgeklappt((bisher) => {
      const neu = new Set(bisher)
      if (neu.has(schluessel)) {
        neu.delete(schluessel)
      } else {
        neu.add(schluessel)
      }
      return neu
    })
  }

  function schalte(schluessel: string) {
    // Die Abgrenzung verengt über beliebig viele Positionen (R826).
    setWahl((bisher) =>
      bisher.includes(schluessel) ? bisher.filter((x) => x !== schluessel) : [...bisher, schluessel],
    )
  }

  const sichten = useMemo(() => {
    const berichte: Bericht[] = ['bilanz', 'guv']
    return berichte.map((bericht) => ({
      bericht,
      // Gewählt werden Positionen; Konten stehen hier nicht, also auch kein
      // Pfeil an einer Position, unter der nur Konten hängen (R1857).
      zeilen: zielzeilen((bild?.knoten ?? []).map((k) => ({ ...k, konten: [] })), bericht, aufgeklappt, ''),
    }))
  }, [bild, aufgeklappt])

  return (
    <Dialogfenster
      titel={t('gliederungswahl.titel.positionen')}
      kopfzusatz={<span className="gedaempft">{herkunft}</span>}
      weite="mittel"
      aufSchliessen={aufSchliessen}
      fuss={
        <>
          <button type="button" onClick={() => aufUebernehmen(wahl)}>
            {t('gliederungswahl.uebernehmen')}
          </button>
          <button type="button" onClick={aufSchliessen}>
            {t('gliederungswahl.abbrechen')}
          </button>
        </>
      }
    >
      <p className="erlaeuterung">
        {t('gliederungswahl.hinweis.positionen')}
      </p>

      {bildabruf.ausgefallen && <Abrufausfall abruf={bildabruf} satz={fehler ?? undefined} />}

      {/* Kein Plan ist eine Auskunft (R1210) — erst, wenn der Abruf gelang. */}
      {!bildabruf.ausgefallen && !bildabruf.laedt && bild === null && (
        <p className="hinweis">{t('mapping.gliederung.leer')}</p>
      )}

      {bild !== null &&
        sichten.map(({ bericht, zeilen }) => (
          <section key={bericht} className="gliederungswahl">
            <h3 className="abschnitt">
              {t(bericht === 'guv' ? 'gliederungswahl.bericht.guv' : 'gliederungswahl.bericht.bilanz')}
            </h3>

            {zeilen.length === 0 ? (
              <p className="erlaeuterung">{t('gliederungswahl.leer')}</p>
            ) : (
              /* **Gezeichnet wie der Bericht** (7.2 Abschnitt 55, R1855). */
              <ul className="wahlbaum berichtsbaum">
                {zeilen.map((z) => (
                  <Baumzeile
                    key={z.knoten.schluessel}
                    art="position"
                    ebene={z.knoten.ebene}
                    rang={z.knoten}
                    pfeil={{
                      offen: z.offen,
                      hatKinder: z.hatKinder,
                      beschriftung: t(z.offen ? 'gliederungswahl.zuklappen' : 'gliederungswahl.aufklappen', {
                        position: `${z.knoten.code} ${beschriftung(z.knoten.namen)}`.trim(),
                      }),
                      aufKlappen: () => klappe(z.knoten.schluessel),
                    }}
                  >
                    <label className="zeileninhalt wahlname">
                      <input
                        type="checkbox"
                        checked={wahl.includes(z.knoten.schluessel)}
                        onChange={() => schalte(z.knoten.schluessel)}
                      />
                      <span className="code">{z.knoten.code}</span>
                      <span>{beschriftung(z.knoten.namen)}</span>
                      <Bisvermerk bis={z.knoten.giltBis} />
                    </label>
                  </Baumzeile>
                ))}
              </ul>
            )}
          </section>
        ))}
    </Dialogfenster>
  )
}
